// src/components/grammar/GrammarTabNavigation.jsx
import React, { useState } from 'react';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GrammarTabNavigation = ({
  pillars,
  activePillar,
  onTabClick,
  isPillarUnlocked,
  isPillarPremium,
  isPillarMastered,
  getPillarProgress,
  isMobile
}) => {
  const [hoveredPillar, setHoveredPillar] = useState(null);
  const { t } = useTranslation();

  // Find current pillar index
  const currentIndex = pillars.findIndex(p => p.id === activePillar);
  
  // Get previous, current, and next pillars
  const visiblePillars = [];
  
  // Previous pillar
  if (currentIndex > 0) {
    visiblePillars.push({
      ...pillars[currentIndex - 1],
      position: 'previous'
    });
  }
  
  // Current pillar
  if (currentIndex >= 0) {
    visiblePillars.push({
      ...pillars[currentIndex],
      position: 'current'
    });
  }
  
  // Next pillar
  if (currentIndex < pillars.length - 1) {
    visiblePillars.push({
      ...pillars[currentIndex + 1],
      position: 'next'
    });
  }

  const containerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: isMobile ? '0.5rem' : '1.5rem', // Reduced from 0.75rem to 0.5rem (8px)
  padding: isMobile ? '0.5rem 0.25rem' : '1.5rem', // Reduced from 1rem 0.5rem
  marginBottom: isMobile ? '1rem' : '1.5rem', // Reduced from 1.5rem
};

return (
  <div style={{ position: 'relative' }}>
    <nav style={containerStyle}>
        {visiblePillars.map((pillar) => {
          const isUnlocked = isPillarUnlocked(pillar.id);
          const isPremium = isPillarPremium ? isPillarPremium(pillar.id) : !isUnlocked;
          const isMastered = isPillarMastered(pillar.id);
          const isActive = activePillar === pillar.id;
          const isHovered = hoveredPillar === pillar.id;
          const isCurrent = pillar.position === 'current';

          // Base styles
          let backgroundColor = '#FFFFFF';
          let textColor = '#000000';
          let borderColor = '#000000';
          
          if (isActive) {
            backgroundColor = '#000000';
            textColor = '#FFFFFF';
            borderColor = '#000000';
          } else if (isPremium) {
            backgroundColor = '#F3FBFA';
            textColor = '#111827';
            borderColor = '#006D77';
          }

          // Size based on position - BIGGER boxes overall
          const isPrevOrNext = pillar.position === 'previous' || pillar.position === 'next';
          
          // Fixed widths for consistent sizing - optimized for mobile
          const width = isMobile 
            ? (isCurrent ? '160px' : '140px') // Reduced from 220px/180px
            : (isCurrent ? '320px' : '240px');
          
          const height = isMobile
            ? (isCurrent ? '72px' : '64px') // Reduced from 100px/85px
            : (isCurrent ? '140px' : '110px');

          const scale = isPrevOrNext ? 0.95 : 1;
          const opacity = isPrevOrNext ? 0.75 : 1;

         const tabStyle = {
  width: width,
  height: height,
  minWidth: width,
  maxWidth: width,
  padding: isMobile ? '0.5rem' : '1.5rem', // Reduced from 1rem to 0.5rem (8px)
  fontSize: isCurrent ? '0.95rem' : '0.85rem',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: isMobile ? '0.5rem' : '1rem', // Reduced from 1rem to 0.5rem on mobile
  border: isMobile ? `2px solid ${borderColor}` : `3px solid ${borderColor}`, // Reduced from 3px to 2px
  borderRadius: isMobile ? '0.75rem' : '1rem', // Reduced from 1rem to 0.75rem (12px)
  background: backgroundColor,
  color: textColor,
  cursor: 'pointer',
  transition: 'all 0.5s',
  position: 'relative',
  overflow: 'hidden',
  opacity: opacity,
  transform: `scale(${scale})`,
  boxShadow: isCurrent ? '0 4px 12px rgba(0, 0, 0, 0.1)' : '0 2px 6px rgba(0, 0, 0, 0.05)',
};

          return (
  <button
    key={pillar.id}
    onClick={() => onTabClick(pillar.id)}
    onMouseEnter={() => setHoveredPillar(pillar.id)}
    onMouseLeave={() => setHoveredPillar(null)}
    style={tabStyle}
  >
    {/* Subtle grid pattern background on each tab */}
<div style={{
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: isActive
  ? `
    linear-gradient(0deg, transparent 24%, rgba(255, 255, 255, 0.04) 25%, rgba(255, 255, 255, 0.04) 26%, transparent 27%, transparent 74%, rgba(255, 255, 255, 0.04) 75%, rgba(255, 255, 255, 0.04) 76%, transparent 77%, transparent),
    linear-gradient(90deg, transparent 24%, rgba(255, 255, 255, 0.04) 25%, rgba(255, 255, 255, 0.04) 26%, transparent 27%, transparent 74%, rgba(255, 255, 255, 0.04) 75%, rgba(255, 255, 255, 0.04) 76%, transparent 77%, transparent)
  `
  : `
    linear-gradient(0deg, transparent 24%, rgba(0, 0, 0, 0.03) 25%, rgba(0, 0, 0, 0.03) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, 0.03) 75%, rgba(0, 0, 0, 0.03) 76%, transparent 77%, transparent),
    linear-gradient(90deg, transparent 24%, rgba(0, 0, 0, 0.03) 25%, rgba(0, 0, 0, 0.03) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, 0.03) 75%, rgba(0, 0, 0, 0.03) 76%, transparent 77%, transparent)
  `,
backgroundSize: isMobile ? '24px 24px' : '40px 40px', // Smaller grid on mobile
opacity: isMobile ? 0.5 : 0.7, // Reduced opacity on mobile
  pointerEvents: 'none',
  zIndex: 0,
}} />

    {/* Top accent line - Royal Blue (only on current/active tab) */}
    {isCurrent && (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: isMobile ? '2px' : '3px', // Reduced from 3px to 2px on mobile
        background: isActive
          ? 'linear-gradient(90deg, #3A86FF 0%, #FFFFFF 50%, #3A86FF 100%)'
          : 'linear-gradient(90deg, #3A86FF 0%, #000000 50%, #3A86FF 100%)',
        zIndex: 1,
      }} />
    )}

    {/* Logami corner decorations - only on current tab - hidden on mobile */}
    {isCurrent && !isMobile && (
      <>
        {/* Top Left Corner */}
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          width: '12px',
          height: '12px',
          borderTop: `2px solid ${isActive ? '#FFFFFF' : '#3A86FF'}`,
          borderLeft: `2px solid ${isActive ? '#FFFFFF' : '#3A86FF'}`,
          borderRadius: '2px 0 0 0',
          zIndex: 1,
          opacity: 0.6,
        }} />

        {/* Bottom Right Corner */}
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          width: '12px',
          height: '12px',
          borderBottom: `2px solid ${isActive ? '#FFFFFF' : '#000000'}`,
          borderRight: `2px solid ${isActive ? '#FFFFFF' : '#000000'}`,
          borderRadius: '0 0 2px 0',
          zIndex: 1,
          opacity: 0.4,
        }} />
      </>
    )}

    {/* Wave fill effect on hover - only for unlocked pillars */}
    {!isActive && (
  <div
    style={{
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: pillar.position === 'previous' ? 'auto' : 0,
      right: pillar.position === 'previous' ? 0 : 'auto',
      width: isHovered ? '100%' : '0%',
      background: isPremium
        ? (pillar.position === 'previous'
          ? 'linear-gradient(to left, #9ad9d0, #39a59a, #006D77)'
          : 'linear-gradient(to right, #9ad9d0, #39a59a, #006D77)')
        : (pillar.position === 'previous'
          ? 'linear-gradient(to left, #9ca3af, #4b5563, #000000)'
          : 'linear-gradient(to right, #9ca3af, #4b5563, #000000)'),
      transition: 'width 0.7s ease-out',
      zIndex: 0
    }}
  />
)}

              {/* Direction indicator for prev/next */}
              {pillar.position === 'previous' && (
                <div
                  style={{
                    position: 'relative',
                    zIndex: 10,
                  }}
                >
                  <ChevronLeft
                    style={{
                      width: isMobile ? (isCurrent ? '1.25rem' : '1rem') : (isCurrent ? '1.5rem' : '1.25rem'),
                      height: isMobile ? (isCurrent ? '1.25rem' : '1rem') : (isCurrent ? '1.5rem' : '1.25rem'),
                      color: isActive
                        ? '#FFFFFF'
                        : isHovered
                          ? '#FFFFFF'
                          : '#000000',
                      transition: 'color 0.5s'
                    }}
                  />
                </div>
              )}

              {/* Icon */}
              <div
                style={{
                  width: isMobile 
                    ? (isCurrent ? '2rem' : '1.5rem') // Reduced: 2rem (32px) for current, 1.5rem (24px) for others
                    : (isCurrent ? '2.5rem' : '2rem'),
                  height: isMobile 
                    ? (isCurrent ? '2rem' : '1.5rem')
                    : (isCurrent ? '2.5rem' : '2rem'),
                  borderRadius: isMobile ? '0.5rem' : '0.75rem', // Reduced from 0.75rem to 0.5rem
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile
                    ? (isCurrent ? '0.875rem' : '0.75rem') // Reduced: 0.875rem (14px) for current, 0.75rem (12px) for others
                    : (isCurrent ? '1.125rem' : '0.95rem'),
                  fontWeight: '700',
                  position: 'relative',
                  zIndex: 10,
                  backgroundColor: isActive
                    ? 'rgba(255, 255, 255, 0.2)'
                    : isHovered
                      ? '#FFFFFF'
                      : isPremium
                        ? '#006D77'
                        : '#000000',
                  color: isActive
                    ? '#FFFFFF'
                    : isHovered
                      ? '#000000'
                      : '#FFFFFF',
                  transition: 'all 0.5s',
                  flexShrink: 0
                }}
              >
                {isMastered ? (
                  <CheckCircle style={{ 
                    width: isMobile ? (isCurrent ? '1rem' : '0.875rem') : (isCurrent ? '1.25rem' : '1rem'), 
                    height: isMobile ? (isCurrent ? '1rem' : '0.875rem') : (isCurrent ? '1.25rem' : '1rem') 
                  }} />
                ) : (
                  pillar.id
                )}
              </div>

             {/* Text */}
<div style={{
  position: 'relative',
  zIndex: 10,
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center'
}}>
  <div
    style={{
      fontWeight: '700',
      fontSize: isMobile
        ? (isCurrent ? '0.875rem' : '0.8125rem') // Reduced: 0.875rem (14px) for current, 0.8125rem (13px) for others
        : (isCurrent ? '1.125rem' : '0.95rem'),
      color: isActive
        ? '#FFFFFF'
        : isHovered
          ? '#FFFFFF'
          : '#000000',
      transition: 'color 0.5s',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '100%',
      marginBottom: isMobile 
        ? (isCurrent ? '0.25rem' : '0.125rem') // Reduced from 0.5rem/0.25rem
        : (isCurrent ? '0.5rem' : '0.25rem'),
      textAlign: 'center'
    }}
  >
    Lesson {pillar.id}
  </div>

  {/* Blue accent line - only on current tab */}
  {isCurrent && (
    <div style={{
      width: isMobile ? '32px' : '40px', // Reduced from 40px to 32px
      height: isMobile ? '2px' : '3px', // Reduced from 3px to 2px
      background: isActive
        ? 'linear-gradient(90deg, #3A86FF 0%, #FFFFFF 50%, #3A86FF 100%)'
        : 'linear-gradient(90deg, #3A86FF 0%, #1a5dc7 100%)',
      borderRadius: '2px',
      margin: isMobile ? '0 auto 0.25rem' : '0 auto 0.5rem', // Reduced from 0.5rem to 0.25rem
      boxShadow: isActive
        ? '0 2px 6px rgba(58, 134, 255, 0.6)'
        : '0 2px 6px rgba(58, 134, 255, 0.4)',
      transition: 'all 0.5s'
    }} />
  )}

  <div
    style={{
      fontSize: isMobile
        ? (isCurrent ? '0.75rem' : '0.6875rem') // Reduced: 0.75rem (12px) for current, 0.6875rem (11px) for others
        : (isCurrent ? '0.8rem' : '0.7rem'),
      opacity: 0.8,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '100%',
      color: isActive
        ? '#FFFFFF'
        : isHovered
          ? '#FFFFFF'
          : '#000000',
      transition: 'color 0.5s',
      textAlign: 'center'
    }}
  >
    {pillar.title}
  </div>
</div>

              {/* Direction indicator for next */}
              {pillar.position === 'next' && (
                <div
                  style={{
                    position: 'relative',
                    zIndex: 10,
                  }}
                >
                  <ChevronRight
                    style={{
                      width: isMobile ? (isCurrent ? '1.25rem' : '1rem') : (isCurrent ? '1.5rem' : '1.25rem'),
                      height: isMobile ? (isCurrent ? '1.25rem' : '1rem') : (isCurrent ? '1.5rem' : '1.25rem'),
                      color: isActive
                        ? '#FFFFFF'
                        : isHovered
                          ? '#FFFFFF'
                          : '#000000',
                      transition: 'color 0.5s'
                    }}
                  />
                </div>
              )}

              {/* Premium badge */}
              {isPremium && (
                <div
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: '#E6F7F4',
                    color: '#006D77',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    zIndex: 20,
                    pointerEvents: 'none',
                    border: '1px solid rgba(0,109,119,0.35)'
                  }}
                >
                  <span>{t('premium.only')}</span>
                </div>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default GrammarTabNavigation;
